import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card, Form, InputGroup, Button } from 'react-bootstrap';

// Import constants if available, otherwise define locally
const formInputsTypes = {
	INPUT_NUMBER: 'INPUT_NUMBER',
	NUMERIC: 'numeric',
	TEXT: 'text',
};

// Default theme colors
const argonTheme = {
	COLORS: {
		DEFAULT: '#32325d',
		INFO: '#17a2b8',
		BG_SIMULATOR: '#f8f9fa',
		INPUT_BG: '#ffffff',
	},
};

const InputNumber = ({ data, handelChange, autoFocus = false }) => {
	const [value, setValue] = useState(
		(data.defaultValue == 0 ? '' : data.defaultValue) ||
			(data?.type === formInputsTypes.INPUT_NUMBER ? 0 : ''),
	);
	const [error, setError] = useState('');

	useEffect(() => {
		if (data?.defaultValue !== undefined) {
			setValue(data.defaultValue == 0 ? '' : data.defaultValue);
		}
	}, [data?.defaultValue]);

	const validateInput = (inputValue) => {
		if (!data?.validation) return true;

		// Handle custom validation function
		if (typeof data.validation === 'function') {
			return data.validation(inputValue);
		}

		// Handle pattern validation
		if (data.validation.pattern) {
			const regex = new RegExp(data.validation.pattern);
			return regex.test(inputValue);
		}

		// Handle min/max validation for numbers
		if (data.type === formInputsTypes.INPUT_NUMBER || data.type === formInputsTypes.NUMERIC) {
			const numValue = Number(inputValue);

			if (data.validation.min !== undefined && numValue < data.validation.min) {
				setError(`La valeur doit être au moins ${data.validation.min}`);
				return false;
			}

			if (data.validation.max !== undefined && numValue > data.validation.max) {
				setError(`La valeur doit être au plus ${data.validation.max}`);
				return false;
			}

			setError('');
			return true;
		}

		return true;
	};
	const handleChange = (e) => {
		const newValue = e.target.value;
		setValue(newValue);

		if (validateInput(newValue)) {
			handelChange(
				data?.nextStepData || data?.nextStep || false,
				`- ${data?.label} : ${newValue || 0}`,
			);
		}
	};

	const increment = () => {
		const newValue = Number(value) + 1;
		setValue(newValue.toString());

		if (validateInput(newValue.toString())) {
			handelChange(data?.nextStepData || data?.nextStep || false, newValue.toString());
		}
	};

	const decrement = () => {
		const newValue = Math.max(0, Number(value) - 1);
		setValue(newValue.toString());

		if (validateInput(newValue.toString())) {
			handelChange(data?.nextStepData || data?.nextStep || false, newValue.toString());
		}
	};

	// Determine input type based on data type
	const inputType =
		data?.type === formInputsTypes.INPUT_NUMBER || data?.type === formInputsTypes.NUMERIC
			? 'number'
			: 'text';

	return (
		<Card
			className='mb-4 border-0 d-flex flex-column'
			style={{
				backgroundColor: argonTheme.COLORS.BG_SIMULATOR,
				borderRadius: '15px',
				padding: '20px',
				minHeight: '200px',
				justifyContent: 'center',
			}}>
			{data?.label && (
				<Card.Title
					className='mb-3 text-center'
					style={{
						color: argonTheme.COLORS.DEFAULT,
						fontSize: '1.1rem',
						fontWeight: '600',
					}}>
					{data.label}
					{data.required && <span className='text-danger ms-1'>*</span>}
				</Card.Title>
			)}

			<Card.Body className='d-flex flex-column align-items-center justify-content-center p-0'>
				<div className='w-100' style={{ maxWidth: '400px' }}>
					{data.type === formInputsTypes.INPUT_NUMBER ? (
						<InputGroup className='mb-3'>
							<Button
								variant='outline-secondary'
								onClick={decrement}
								disabled={data.min !== undefined && Number(value) <= data.min}
								style={{
									borderColor: argonTheme.COLORS.INFO,
									color: argonTheme.COLORS.INFO,
									width: '60px',
								}}>
								<span style={{ fontSize: '1.5rem' }}>-</span>
							</Button>

							<Form.Control
								type={inputType}
								value={value}
								onChange={handleChange}
								placeholder={data?.placeholder || ''}
								// autoFocus={autoFocus}
								className='text-center'
								style={{
									borderColor: argonTheme.COLORS.INFO,
									fontSize: '1.5rem',
									height: '60px',
									backgroundColor: argonTheme.COLORS.INPUT_BG,
								}}
								min={data?.min}
								max={data?.max}
								step={data?.step || 1}
							/>

							<Button
								variant='outline-secondary'
								onClick={increment}
								disabled={data.max !== undefined && Number(value) >= data.max}
								style={{
									borderColor: argonTheme.COLORS.INFO,
									color: argonTheme.COLORS.INFO,
									width: '60px',
								}}>
								<span style={{ fontSize: '1.5rem' }}>+</span>
							</Button>
						</InputGroup>
					) : (
						<Form.Control
							type={inputType}
							value={value}
							onChange={handleChange}
							placeholder={data?.placeholder || ''}
							// autoFocus={autoFocus}
							className='text-center'
							style={{
								borderColor: argonTheme.COLORS.INFO,
								fontSize: '1.5rem',
								height: '60px',
								backgroundColor: argonTheme.COLORS.INPUT_BG,
								maxWidth: '300px',
								margin: '0 auto',
							}}
							min={data?.min}
							max={data?.max}
							step={data?.step || 1}
						/>
					)}

					{error && <div className='text-danger text-center mt-2'>{error}</div>}

					{data?.description && (
						<div className='text-muted text-center mt-3'>{data.description}</div>
					)}

					{(data.min !== undefined || data.max !== undefined) && (
						<div className='text-muted text-center small mt-2'>
							{data.min !== undefined && `Min: ${data.min}`}
							{data.min !== undefined && data.max !== undefined && ' - '}
							{data.max !== undefined && `Max: ${data.max}`}
						</div>
					)}
				</div>
			</Card.Body>
		</Card>
	);
};

InputNumber.propTypes = {
	data: PropTypes.shape({
		id: PropTypes.string,
		label: PropTypes.string,
		type: PropTypes.string,
		placeholder: PropTypes.string,
		defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		required: PropTypes.bool,
		description: PropTypes.string,
		min: PropTypes.number,
		max: PropTypes.number,
		step: PropTypes.number,
		nextStep: PropTypes.any,
		nextStepData: PropTypes.any,
		validation: PropTypes.oneOfType([
			PropTypes.func,
			PropTypes.shape({
				pattern: PropTypes.string,
				min: PropTypes.number,
				max: PropTypes.number,
			}),
		]),
	}).isRequired,
	handelChange: PropTypes.func.isRequired,
	autoFocus: PropTypes.bool,
};

InputNumber.defaultProps = {
	autoFocus: false,
};

export default InputNumber;
